/**
 * GenericMetricWidget — data-driven chart component for the Exec dashboard.
 *
 * Replaces all bespoke section components (SpeedToContractSection, etc.)
 * with a single component that renders any metric × dimension combination
 * as bar, line, heatmap, or table — all configured via widget.config.
 *
 * Supports drill-down: clicking a bar/row navigates to the next subdivision
 * (e.g., agency → macom → subcom1 → office; FY → FQ).
 */
import { useEffect, useState, useCallback, useMemo } from 'react'
import { ChevronRight, RefreshCw } from 'lucide-react'
import { useExecDashboard } from '../../../contexts/ExecDashboardContext'
import { usePerformanceQuery } from './usePerformanceQuery'
import BarChartRenderer from './BarChartRenderer'
import LineChartRenderer from './LineChartRenderer'
import HeatmapRenderer from './HeatmapRenderer'
import TableRenderer from './TableRenderer'
import MapChartRenderer from './MapChartRenderer'
import BulletRenderer from './BulletRenderer'
import TreemapRenderer from './TreemapRenderer'
import SlopeChartRenderer from './SlopeChartRenderer'
import BoxplotRenderer from './BoxplotRenderer'
import CalendarHeatmapRenderer from './CalendarHeatmapRenderer'
import ScatterRenderer from './ScatterRenderer'
import SankeyRenderer from './SankeyRenderer'
import WidgetState from '../WidgetState'
import CoverageBadge from '../CoverageBadge'
import { getMeasureDoc } from '../measure-docs'
import { setWidgetExportRows, clearWidgetExportRows } from '../widgetDataRegistry'
import { usePeerContext } from './usePeerContext'
import type { DashboardWidget } from '../../../types/exec'
import type {
  GenericWidgetConfig,
  GroupByDimension,
  MetricKey,
  ChartType,
  DrillLevel,
} from './types'
import { DRILL_HIERARCHY, DIMENSION_LABELS } from './types'

/** Default configs for measures that don't have explicit config */
const MEASURE_DEFAULTS: Record<string, Partial<GenericWidgetConfig>> = {
  office_performance: { group_by: 'fiscal_year', metrics: ['award_count', 'total_obligated'], chart_type: 'bar' },
  competition_health: { group_by: 'fiscal_year', metrics: ['full_open_pct', 'not_competed_pct'], chart_type: 'bar' },
  small_biz_engagement: { group_by: 'fiscal_year', metrics: ['count_8a', 'count_hubzone', 'count_sdvosb', 'count_wosb'], chart_type: 'bar' },
  contract_type_trends: { group_by: 'vehicle_category', metrics: ['award_count'], chart_type: 'bar', series_by: 'fiscal_year' },
  ot_prevalence: { group_by: 'fiscal_year', metrics: ['award_count'], chart_type: 'bar', series_by: 'is_ot' },
  acq_performance: { group_by: 'agency', metrics: ['total_obligated'], chart_type: 'bar' },
  activity_by_vertical: { group_by: 'psc_level1_code', metrics: ['total_obligated'], chart_type: 'bar' },
  // Separate-table measures are handled by the legacy components
}

interface Props {
  widget: DashboardWidget
}

export default function GenericMetricWidget({ widget }: Props) {
  const { filters: dashboardFilters, goals, chartGroup } = useExecDashboard()

  // ── Build effective config from widget + defaults + dashboard filters ──
  const config = useMemo((): GenericWidgetConfig => {
    const defaults = MEASURE_DEFAULTS[widget.measure] || {}
    const wc = widget.config || {}
    return {
      group_by: (wc.group_by || defaults.group_by || 'fiscal_year') as GroupByDimension,
      metrics: (wc.metrics || defaults.metrics || ['award_count']) as MetricKey[],
      series_by: (wc.series_by || defaults.series_by) as GroupByDimension | undefined,
      chart_type: (wc.chart_type || defaults.chart_type || 'bar') as ChartType,
      fiscal_year: wc.fiscal_year ?? dashboardFilters.fiscal_year,
      fiscal_quarter: wc.fiscal_quarter as number | undefined,
      sub_agency_code: (wc.sub_agency_code || dashboardFilters.sub_agency_code) as string | undefined,
      office_code: (wc.office_code || dashboardFilters.office_code) as string | undefined,
      psc_level1_code: wc.psc_level1_code as string | undefined,
      color_of_money: wc.color_of_money as string | undefined,
      vehicle_category: wc.vehicle_category as string | undefined,
      extent_competed: wc.extent_competed as string | undefined,
      macom_code: wc.macom_code as string | undefined,
      agency_code: wc.agency_code as string | undefined,
      portfolio_group: wc.portfolio_group as string | undefined,
      state_code: wc.state_code as string | undefined,
      congressional_district: wc.congressional_district as string | undefined,
      location_kind: wc.location_kind as 'recipient' | 'pop' | undefined,
      sort_by: (wc.sort_by || defaults.sort_by) as string | undefined,
      limit: (wc.limit || defaults.limit || 50) as number,
      size_metric: wc.size_metric as MetricKey | undefined,
      as_rank: wc.as_rank as boolean | undefined,
    }
  }, [widget.config, widget.measure, dashboardFilters])

  // ── Drill-down state ──────────────────────────────────────────────
  const [drillStack, setDrillStack] = useState<DrillLevel[]>([])

  // Chart type comes from the saved widget config (set per preset, editable
  // in the WidgetConfigEditor "Advanced" panel). Phase A3 removed the inline
  // toolbar toggle so chart shape stays a deliberate, persisted choice rather
  // than ephemeral session state.
  const chartType = config.chart_type

  // Current effective group_by (may be overridden by drill-down)
  const effectiveGroupBy = drillStack.length > 0
    ? drillStack[drillStack.length - 1].dimension
    : config.group_by

  // Build drill filter overrides (e.g., parent_code from previous drill level)
  const drillFilters = useMemo((): Record<string, string | number> | undefined => {
    if (drillStack.length === 0) return undefined
    const top = drillStack[drillStack.length - 1]
    if (top.code) {
      return { parent_code: top.code }
    }
    return undefined
  }, [drillStack])

  // Build the effective config with drill overrides
  const effectiveConfig = useMemo((): GenericWidgetConfig => ({
    ...config,
    group_by: effectiveGroupBy,
    // Clear series_by when drilling to avoid overly complex queries
    series_by: drillStack.length > 0 ? undefined : config.series_by,
  }), [config, effectiveGroupBy, drillStack])

  // ── Fetch data ────────────────────────────────────────────────────
  const { data, loading, error, refresh } = usePerformanceQuery(effectiveConfig, drillFilters)

  // Register the current rows in the export registry so WidgetCard's
  // WidgetExportMenu can offer CSV export without prop-drilling data
  // through React Grid Layout's wrapping divs (Phase J).
  useEffect(() => {
    if (data) setWidgetExportRows(widget.id, data as Array<Record<string, unknown>>)
    return () => clearWidgetExportRows(widget.id)
  }, [widget.id, data])

  // ── Peer-line overlay (Phase H1) ──────────────────────────────────
  // For time-series widgets scoped to a portfolio, fetch peer benchmarks
  // (All DoD + parent sub-agency) and pass them through as groupComparisons
  // so the renderers overlay them as dashed lines.
  const peerEnabled = (
    !!widget.config?.portfolio_group &&
    (effectiveConfig.group_by === 'fiscal_year' || effectiveConfig.group_by === 'fiscal_quarter') &&
    (chartType === 'line' || chartType === 'bar')
  )
  const peerSeries = usePeerContext(
    widget.config?.portfolio_group as string | undefined,
    effectiveConfig.fiscal_year,
    peerEnabled,
  )

  // ── Goal-line overlay (Phase D2) ──────────────────────────────────
  // Find a goal scoped to this widget's measure + (optionally) its portfolio
  // so renderers can draw a horizontal target line. Only line/bar renderers
  // currently consume this; other renderers ignore the prop.
  const goalForMeasure = useMemo(() => {
    const widgetPg = (widget.config?.portfolio_group as string | undefined) ?? null
    const candidate = goals.find((g) => {
      if (g.measure !== widget.measure) return false
      // If the widget is portfolio-scoped, prefer goals on that exact portfolio.
      if (widgetPg && g.portfolio_group && g.portfolio_group !== widgetPg) return false
      return true
    })
    if (!candidate) return undefined
    const unit = candidate.target_value.unit
    const suffix = unit === 'percent' ? '%' : unit === 'days' ? 'd' : unit === 'dollars' ? '$' : ''
    return {
      value: candidate.target_value.value,
      label: `Target: ${candidate.target_value.value}${suffix}`,
      direction: candidate.target_value.direction,
    }
  }, [goals, widget.measure, widget.config])

  // ── Drill-down handler ────────────────────────────────────────────
  const canDrill = !!DRILL_HIERARCHY[effectiveGroupBy]

  const handleDrillDown = useCallback((code: string, label: string) => {
    const nextDim = DRILL_HIERARCHY[effectiveGroupBy]
    if (!nextDim) return
    setDrillStack((prev) => [...prev, { dimension: nextDim, code, label }])
  }, [effectiveGroupBy])

  const handleBreadcrumbClick = useCallback((index: number) => {
    // Click on root: clear all drill state
    if (index < 0) {
      setDrillStack([])
      return
    }
    // Click on a breadcrumb: go back to that level
    setDrillStack((prev) => prev.slice(0, index + 1))
  }, [])

  // ── Render ────────────────────────────────────────────────────────
  if (error) {
    return <WidgetState state="error" message={error} onRetry={refresh} />
  }

  // Toolbar only renders when there's a drill trail to navigate. At the
  // root, the dimension label duplicates the WidgetCard header and the
  // refresh button is redundant with the dashboard-wide refresh, so we
  // hand all available height to the chart instead.
  const showToolbar = drillStack.length > 0

  return (
    <div className="flex flex-col h-full">
      {showToolbar && (
        <div className="flex items-center justify-between px-1 py-1 shrink-0">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-0.5 text-[11px] text-gray-500 min-w-0 overflow-hidden">
            <button
              onClick={() => handleBreadcrumbClick(-1)}
              className="hover:text-blue-600 truncate"
            >
              {DIMENSION_LABELS[config.group_by]}
            </button>
            {drillStack.map((level, i) => (
              <span key={i} className="flex items-center gap-0.5 min-w-0">
                <ChevronRight size={10} className="shrink-0 text-gray-300" />
                <button
                  onClick={() => handleBreadcrumbClick(i)}
                  className={`hover:text-blue-600 truncate ${
                    i === drillStack.length - 1 ? 'font-medium text-gray-700 dark:text-gray-200' : ''
                  }`}
                >
                  {level.label}
                </button>
              </span>
            ))}
          </div>

          <div className="flex items-center gap-0.5 shrink-0 ml-2">
            <button
              onClick={refresh}
              title="Refresh data"
              className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 ml-0.5"
            >
              <RefreshCw size={13} />
            </button>
          </div>
        </div>
      )}

      {/* Coverage / FAR-only badge — surfaced just below the toolbar so the
          leader sees denominator caveats inline with the chart (Phase E2). */}
      {(() => {
        // Compute totals once across visible rows — relies on the API returning
        // these denominator-metadata columns (PR #91).
        let awardCount = 0
        let matched = 0
        let farCount = 0
        let otCount = 0
        for (const row of data ?? []) {
          awardCount += Number(row.award_count ?? 0) || 0
          matched += Number(row.awards_with_solicitation_link ?? 0) || 0
          farCount += Number(row.far_award_count ?? 0) || 0
          otCount += Number(row.ot_award_count ?? 0) || 0
        }
        const isSpeedMeasure = widget.measure === 'speed_to_contract'
        const isFarOnly = !!getMeasureDoc(widget.measure).farOnly
        const sliceIsAllOt = farCount === 0 && otCount > 0
        const sliceIsMixed = farCount > 0 && otCount > 0
        return (
          <div className="flex items-center gap-1.5 flex-wrap px-1 pb-1">
            {isSpeedMeasure && awardCount > 0 && (
              <CoverageBadge mode="match-rate" matchedCount={matched} totalCount={awardCount} />
            )}
            {isFarOnly && sliceIsAllOt && (
              <CoverageBadge mode="far-only-all-ot" />
            )}
            {isFarOnly && sliceIsMixed && (
              <CoverageBadge mode="far-only-mixed" otCount={otCount} />
            )}
          </div>
        )
      })()}

      {/* Chart area */}
      <div className="flex-1 min-h-0 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-gray-900/60 z-10">
            <WidgetState state="loading" />
          </div>
        )}
        {!loading && (data?.length ?? 0) === 0 && (
          <WidgetState state="empty" />
        )}

        {/* Bar is the default AND the fallback: a genuinely unknown chart_type
            (e.g. a viz saved by a newer build, viewed via the shared dev DB)
            renders as a bar rather than a silent blank. Every type that has a
            dedicated renderer below MUST be excluded here, or it double-renders
            a stray bar on top of (or instead of) its real chart. */}
        {!['line', 'heatmap', 'table', 'map', 'bullet', 'treemap', 'slope', 'boxplot', 'calendar', 'scatter', 'sankey'].includes(chartType) && (
          <BarChartRenderer
            data={data}
            groupBy={effectiveGroupBy}
            metrics={effectiveConfig.metrics}
            seriesBy={effectiveConfig.series_by}
            onDrillDown={canDrill ? handleDrillDown : undefined}
            goalLine={goalForMeasure}
            chartGroup={chartGroup}
            groupComparisons={peerSeries}
            primaryLabel="Your Portfolio"
          />
        )}
        {chartType === 'line' && (
          <LineChartRenderer
            data={data}
            groupBy={effectiveGroupBy}
            metrics={effectiveConfig.metrics}
            seriesBy={effectiveConfig.series_by}
            goalLine={goalForMeasure}
            chartGroup={chartGroup}
            groupComparisons={peerSeries}
            primaryLabel="Your Portfolio"
          />
        )}
        {chartType === 'heatmap' && (
          <HeatmapRenderer
            data={data}
            groupBy={effectiveGroupBy}
            metrics={effectiveConfig.metrics}
            seriesBy={effectiveConfig.series_by}
            onDrillDown={canDrill ? handleDrillDown : undefined}
            chartGroup={chartGroup}
          />
        )}
        {chartType === 'table' && (
          <TableRenderer
            data={data}
            groupBy={effectiveGroupBy}
            metrics={effectiveConfig.metrics}
            seriesBy={effectiveConfig.series_by}
            onDrillDown={canDrill ? handleDrillDown : undefined}
          />
        )}
        {chartType === 'map' && (
          <MapChartRenderer
            data={data}
            groupBy={effectiveGroupBy}
            metrics={effectiveConfig.metrics}
            onDrillDown={canDrill ? handleDrillDown : undefined}
            stateFilter={typeof effectiveConfig.state_code === 'string' ? effectiveConfig.state_code : undefined}
          />
        )}
        {chartType === 'bullet' && (
          <BulletRenderer
            data={data}
            groupBy={effectiveGroupBy}
            metrics={effectiveConfig.metrics}
          />
        )}
        {chartType === 'treemap' && (
          <TreemapRenderer
            data={data}
            groupBy={effectiveGroupBy}
            metrics={effectiveConfig.metrics}
            onDrillDown={canDrill ? handleDrillDown : undefined}
          />
        )}
        {chartType === 'slope' && (
          <SlopeChartRenderer
            data={data}
            xAxis={effectiveConfig.series_by ?? 'fiscal_year'}
            groupBy={effectiveGroupBy}
            metrics={effectiveConfig.metrics}
            asRank={Boolean(effectiveConfig.as_rank)}
          />
        )}
        {chartType === 'boxplot' && (
          <BoxplotRenderer
            data={data}
            groupBy={effectiveGroupBy}
          />
        )}
        {chartType === 'calendar' && (
          <CalendarHeatmapRenderer
            data={data}
            metrics={effectiveConfig.metrics}
            chartGroup={chartGroup}
          />
        )}
        {chartType === 'scatter' && (
          <ScatterRenderer
            data={data}
            groupBy={effectiveGroupBy}
            metrics={effectiveConfig.metrics}
            sizeMetric={effectiveConfig.size_metric as MetricKey | undefined}
            onDrillDown={canDrill ? handleDrillDown : undefined}
          />
        )}
        {chartType === 'sankey' && (
          <SankeyRenderer
            data={data}
            metrics={effectiveConfig.metrics}
            // For Sankey: group_by is the source dim, series_by is the target
            // dim. /performance/ is fetched with group_by=source,target so each
            // row carries both — the renderer pivots into nodes + links.
            sourceField={effectiveConfig.group_by}
            targetField={effectiveConfig.series_by}
          />
        )}
      </div>
    </div>
  )
}
